import { useCallback, useEffect, useMemo, useState } from 'react'
import type { MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIsPlatformOwner } from '@/shared/auth/useIsPlatformOwner'
import { usePermissions } from '@/shared/auth/usePermissions'
import type { NavItem } from './navItems'
import { MENU_MODULE_ENTRIES, fetchHiddenModules } from '@/features/menu/menuApi'
import { queryKeys } from '@/shared/api/queryKeys'
import { NAV, NAV_GROUPS, findNavGroupKey, findNavItem, isNavGroup } from './navItems'

const OPEN_GROUP_KEY = 'xsib.sidebarOpenGroup'

function readStoredGroup(): string | null {
  try {
    return localStorage.getItem(OPEN_GROUP_KEY)
  } catch {
    return null
  }
}

function storeGroup(key: string | null): void {
  try {
    if (key) localStorage.setItem(OPEN_GROUP_KEY, key)
    else localStorage.removeItem(OPEN_GROUP_KEY)
  } catch {
    // localStorage yopiq bo'lsa menyu baribir ishlaydi, faqat esda qolmaydi
  }
}

export interface SidebarMenu {
  items: MenuProps['items']
  selectedKey: string
  /** `collapsed` bo'lganda antd o'zi boshqaradi (suzuvchi oyna) — bu holda `undefined` */
  openKeys: string[] | undefined
  onOpenChange: MenuProps['onOpenChange']
  onClick: MenuProps['onClick']
}

/**
 * Chap navigatsiya menyusining MANTIG'I — desktop (`AppSider`, doimiy
 * ustun) va mobil (`MobileNavDrawer`, suzuvchi panel) ORASIDA BAHAM
 * KO'RILADI. Ikkalasi bir xil menyuni ikki xil qobiqda ko'rsatadi, ya'ni
 * ruxsat filtri, akkordeon holati va tanlangan element BIR JOYDA yozilishi
 * kerak — aks holda ikkalasi asta-sekin bir-biridan farqlanib ketardi.
 */
export function useSidebarMenu(collapsed: boolean): SidebarMenu {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { can, canAny } = usePermissions()
  const isPlatformOwner = useIsPlatformOwner()

  /**
   * Bitta bandni shu foydalanuvchi ko'radimi.
   *
   * ⚠️ UCH shart va ular VA (AND) bilan bog'lanadi. `permission`
   * ro'yxati ichida esa YOKI (OR) — uning eski semantikasi
   * o'zgarmaydi.
   *
   * ⚠️ `allPermissions` — B11 da qo'shildi va u ro'yxat ichida ham VA
   * degani. Sabab «Kampaniyalar» bandida: sahifa ikki huquqqa
   * tayanadi (`campaign.manage` endpoint uchun, `venue.read` esa
   * server qatorlarni `VenueScopeGuard` bilan filtrlagani uchun), va
   * ikkinchisisiz ro'yxat CHALA qaytardi. `permission` ga ikkinchi
   * kodni qo'shish YOKI degani bo'lardi — ya'ni nuqsonni
   * yomonlashtirardi.
   *
   * `globalScopeOnly` faqat platforma darajasidagi bo'limlar uchun:
   * huquqning o'zi yetmaydi, chunki rollar dinamik (`NavItem`
   * ta'rifidagi izoh).
   *
   * ⚠️ GURUHDAGI va YOLG'IZ banddagi filtr AYNAN BIR XIL bo'lishi
   * shart. Ikki joyda ikki nusxa yozilsa, yolg'iz bandlar (bugun —
   * «Boshqaruv paneli» va «Hisobotlar») jimgina boshqa qoida bo'yicha
   * ko'rinadigan bo'lib qolardi.
   */
  const canSee = useCallback(
    (item: NavItem) => {
      if (item.globalScopeOnly && !isPlatformOwner) return false
      if (item.allPermissions && !item.allPermissions.every((p) => can(p))) return false
      return !item.permission || canAny(...item.permission)
    },
    [can, canAny, isPlatformOwner],
  )

  // Yashirish faqat qulaylik: huquq emas, sahifa manzili ochiq qoladi; so'rov yiqilsa menyu to'liq ko'rinadi.
  const hiddenModules = useQuery({
    queryKey: queryKeys.tenantMenu,
    queryFn: ({ signal }) => fetchHiddenModules(signal),
    staleTime: 10 * 60_000,
  })
  const hidden = useMemo(() => {
    const entries = (hiddenModules.data ?? []).map((module) => MENU_MODULE_ENTRIES[module])
    return {
      groups: new Set(entries.flatMap((entry) => entry.groups ?? [])),
      paths: new Set(entries.flatMap((entry) => entry.paths ?? [])),
    }
  }, [hiddenModules.data])

  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.filter((group) => !hidden.groups.has(group.key))
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => canSee(item) && !hidden.paths.has(item.path)),
        }))
        .filter((group) => group.items.length > 0),
    [canSee, hidden],
  )

  /**
   * antd elementlari — `NAV` TARTIBIDA.
   *
   * ⚠️ Guruhlar va yolg'iz bandlar ARALASH turadi va tartib `NAV`
   * dagi bilan bir xil bo'lishi kerak: «Hisobotlar» ataylab o'rtada,
   * operatsion bo'limlardan keyin (`navItems.tsx` dagi izoh). Avval
   * guruhlarni, keyin bandlarni chizish o'sha joylashuvni buzardi.
   */
  const items = useMemo<MenuProps['items']>(
    () =>
      NAV.flatMap((entry) => {
        if (!isNavGroup(entry)) {
          return canSee(entry)
            ? [{ key: entry.path, icon: entry.icon, label: t(entry.labelKey) }]
            : []
        }
        const group = visibleGroups.find((g) => g.key === entry.key)
        if (!group) return []
        return [
          {
            key: group.key,
            icon: group.icon,
            label: t(group.labelKey),
            children: group.items.map((item) => ({
              key: item.path,
              icon: item.icon,
              label: t(item.labelKey),
            })),
          },
        ]
      }),
    [visibleGroups, canSee, t],
  )

  const selectedKey = useMemo(() => findNavItem(location.pathname)?.path ?? '/', [location.pathname])
  const activeGroup = useMemo(() => findNavGroupKey(location.pathname), [location.pathname])

  // ⚠️ SAQLANGAN KALIT ENDIGINA ESKIRGAN BO'LISHI MUMKIN: 2026-08-13 da
  // to'rtta guruh yo'qoldi (`main`, `reports`, `agents`, `attendance`).
  // Brauzerida o'shalardan biri saqlanib qolgan operator uchun quyidagi
  // tekshiruv MAJBURIY — usiz akkordeon mavjud bo'lmagan guruhni
  // ochmoqchi bo'lib, hech qaysi bo'lim ochilmasdan qolardi.
  const [openKey, setOpenKey] = useState<string | null>(() => {
    const stored = readStoredGroup()
    if (stored && NAV_GROUPS.some((g) => g.key === stored)) return stored
    return NAV_GROUPS[0]?.key ?? null
  })

  useEffect(() => {
    if (activeGroup) setOpenKey(activeGroup)
  }, [activeGroup])

  useEffect(() => {
    storeGroup(openKey)
  }, [openKey])

  const effectiveOpenKey =
    openKey && visibleGroups.some((g) => g.key === openKey) ? openKey : (visibleGroups[0]?.key ?? null)

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const next = keys.find((key) => key !== effectiveOpenKey)
    setOpenKey(typeof next === 'string' ? next : null)
  }

  return {
    items,
    selectedKey,
    openKeys: collapsed ? undefined : effectiveOpenKey ? [effectiveOpenKey] : [],
    onOpenChange: collapsed ? undefined : handleOpenChange,
    onClick: ({ key }) => navigate(key),
  }
}
